import Button from '@material-ui/core/Button'
import React, { useState, useMemo } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogActions from '@material-ui/core/DialogActions'
import { useSession } from '../../Organisms/Authentication Dialog/SessionProvider'
import { auth_config } from '../../../features/API/auth_config'

export function DeleteQuestionnaireButton({ questionnaireKey, onDeleteSuccess, onDelete }) {
  const { isAuthenticated, jwt, user } = useSession()
  const [open, setOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState(null)
  
  // Check if user is admin
  // Try to get access_level from user object first, then check JWT token scope
  const isAdmin = useMemo(() => {
    if (user?.access_level === 'admin') {
      return true
    }
    // If access_level is not in user object, check JWT token scope
    if (jwt) {
      try {
        const payload = JSON.parse(atob(jwt.split('.')[1]))
        // Check if user has 'umoresearcher' scope (which indicates admin)
        const scope = payload.scope || []
        const hasResearcherScope = Array.isArray(scope) 
          ? scope.includes('umoresearcher')
          : scope.split(' ').includes('umoresearcher')
        
        // Also check metadata.access_level if available
        if (payload.metadata?.access_level === 'admin') {
          return true
        }
        
        return hasResearcherScope
      } catch (e) {
        // If JWT parsing fails, assume not admin
        return false
      }
    }
    return false
  }, [user, jwt])

  const handleClickOpen = () => {
    setOpen(true)
    setError(null)
  }

  const handleClose = () => {
    if (!isDeleting) {
      setOpen(false)
      setError(null)
    }
  }

  const handleDelete = async () => {
    if (!isAuthenticated || !questionnaireKey) {
      setError('Not authenticated or questionnaire key missing')
      return
    }
    
    if (!isAdmin) {
      setError('Only administrators can delete questionnaires')
      return
    }

    setIsDeleting(true)
    setError(null)

    try {
      const unirest = require('unirest')
      const response = await new Promise((resolve, reject) => {
        unirest('DELETE', auth_config.base + '/api/v1/questionnaire/' + questionnaireKey)
          .headers({
            Authorization: `Bearer ${jwt}`,
            Accept: 'application/json',
          })
          .end(function (res) {
            if (res.error) {
              reject(res)
            } else {
              resolve(res)
            }
          })
      })

      if (response.status >= 200 && response.status < 300) {
        setOpen(false)
        if (onDelete) {
          onDelete()
        }
        if (onDeleteSuccess) {
          onDeleteSuccess()
        }
        // Reload the page to refresh the questionnaire list and clear selection
        setTimeout(() => {
          window.location.reload()
        }, 500)
      } else {
        setError(
          response.body?.result || `Failed to delete questionnaire (${response.status})`
        )
      }
    } catch (error) {
      setError(
        error.body?.result ||
          error.message ||
          'An error occurred while deleting the questionnaire'
      )
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Button
        color='secondary'
        data-cy={'deleteQuestionnaire'}
        id={'deleteQuestionnaire'}
        onClick={handleClickOpen}
        startIcon={<DeleteIcon />}
        disabled={!isAuthenticated || !questionnaireKey || !isAdmin}
        title={!isAdmin && isAuthenticated ? 'Only administrators can delete questionnaires' : ''}
      >
        Delete
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'Do you want to delete this questionnaire?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            The questionnaire will be deleted permanently and cannot be restored.
            {error && (
              <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id={'noToDelete'}
            onClick={handleClose}
            color='primary'
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            id={'yesToDelete'}
            onClick={handleDelete}
            color='secondary'
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
